import React, { useEffect, useState, useCallback } from 'react';
import { Spinner, Alert } from 'flowbite-react';
import API_ENDPOINTS, { buildEndpoint } from '../../../services/api/endpoints';
import api from '../../../services/api/axiosConfig';
import errorHandler from '../../../utils/errorHandler';
import { useParams } from 'react-router-dom';
import Breadcrumb from '../../dashboard/pages/components/BreadCrumb';
import PageHeading from '../../../components/PageHeading';

const EmailDetailsPage = () => {
    const { id } = useParams(); // This 'id' MUST match the parameter in your Route path (e.g., '/customer-email-detail/:id')
    const [emailData, setEmailData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const breadcrumbItems = [
        { label: 'Dashboard', url: '/dashboard' },
        { label: 'Customer Email', url: '/dashboard/customer-email' },
        { label: 'Email ', url: '#', current: true }
    ];

    // Helper function to format dates
    const formatDateTime = (isoString) => {
        if (!isoString) return '-';
        try {
            return new Date(isoString).toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
        } catch (e) {
            console.error("Error formatting date:", e);
            return isoString; // Return original if formatting fails
        }
    };

    // Function to mark the email as read
    const markEmailAsRead = useCallback(async (emailLogId) => {
        if (!emailLogId) return;

        try {
            // Assuming API_ENDPOINTS.PANEL.CUSTOMER.MARK_MAIL_READ is configured
            // and expects a POST or PUT request to update the seen_at timestamp
            const markReadUrl = buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.MARK_MAIL_READ, { id: emailLogId });
            await api.post(markReadUrl); // Or api.put() depending on your backend
            console.log(`Email ID ${emailLogId} marked as read.`);
            // You might want to refresh the emailData here if the backend response includes the updated 'seen_at'
            // For now, we rely on the list page to refetch.
        } catch (err) {
            console.error('Error marking email as read:', err);
            // Optionally, handle error (e.g., show a toast notification) but don't block display
        }
    }, []); // No dependencies needed for useCallback if it just takes the ID

    const fetchEmailDetails = useCallback(async () => {
        if (!id) {
            setEmailData(null);
            setError('No email log ID provided to fetch details.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const apiUrl = buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.MAIL_DETAILS, { id: id });
            const response = await api.get(apiUrl);

            if (response.data.success) {
                const fetchedData = response.data.data;
                setEmailData(fetchedData);

                // If the email is currently unread, mark it as read
                if (!fetchedData.seen_at) {
                    markEmailAsRead(id);
                }

            } else {
                setError(response.data.message || 'Failed to fetch email details.');
            }
        } catch (err) {
            console.error('Error fetching email details:', err);
            errorHandler(err, setError);
        } finally {
            setLoading(false);
        }
    }, [id, markEmailAsRead]); // Added markEmailAsRead to dependencies

    useEffect(() => {
        fetchEmailDetails();
    }, [fetchEmailDetails]);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spinner size="xl" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert color="failure" onDismiss={() => setError(null)} className="mb-4">
                <span className="font-medium">Error!</span> {error}
            </Alert>
        );
    }

    return (
        <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            {!emailData ? (
                <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                    No email details available. Please select an email.
                </div>
            ) : (
                <div className="p-6">
                    <Breadcrumb items={breadcrumbItems} />
                    <PageHeading value="Email Details" />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-gray-700 dark:text-gray-300">
                        {/* Customer Full Name */}
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Customer Name:</span>
                            <span className="text-lg font-medium">{emailData.full_name || 'N/A'}</span>
                        </div>
                        {/* Recipient Email */}
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Recipient Email:</span>
                            <span className="text-lg font-medium text-blue-600 dark:text-blue-400">{emailData.email || 'N/A'}</span>
                        </div>

                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Subject:</span>
                            <span className="text-lg font-medium">{emailData.subject || 'N/A'}</span>
                        </div>

                        {/* Changed to "Read Status" */}
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Read Status:</span>
                            <span className="text-lg font-medium">{emailData.seen_at ? 'Read' : 'Unread'}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Last Read At:</span>
                            <span className="text-lg font-medium">{formatDateTime(emailData.seen_at)}</span>
                        </div>

                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Open Times:</span>
                            <span className="text-lg font-medium">{emailData.open_times !== undefined ? emailData.open_times : 'N/A'}</span>
                        </div>
                        <div className="flex flex-col">
                            <span className="font-semibold text-gray-500 dark:text-gray-400 text-sm">Created At:</span>
                            <span className="text-lg font-medium">{formatDateTime(emailData.created_at)}</span>
                        </div>
                    </div>

                    {/* Message (description) Field */}
                    <div className="mt-8">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 border-b pb-2">Message Content</h3>
                        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                            {/* Using dangerouslySetInnerHTML for HTML content */}
                            <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200"
                                 dangerouslySetInnerHTML={{ __html: emailData.message || 'No message content available.' }}>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailDetailsPage;